<template>
    <div class="max-w-3xl mx-auto p-4 flex flex-col gap-8">
        <div class="flex flex-col gap-1">
            <h1 class="text-2xl font-semibold text-theme-base">
                Funciones de tu negocio
            </h1>
            <p class="text-sm text-theme-muted">
                Activa o apaga las funciones que incluye tu plan. Las que tienen
                candado se habilitan al cambiar de plan.
            </p>
        </div>

        <section v-for="group in groups" :key="group.id" class="flex flex-col">
            <h2
                class="text-lg font-semibold text-theme-primary border-b-2 border-theme-primary pb-2"
            >
                {{ group.label }}
            </h2>
            <div
                v-for="item in group.features"
                :key="item.feature"
                class="flex items-center justify-between gap-4 border-b border-theme-border py-4"
                :class="{ 'opacity-70': item.comingSoon || !hasFeature(item) }"
            >
                <div class="flex flex-col gap-0.5">
                    <p class="font-medium text-theme-base">
                        {{ item.label }}
                    </p>
                    <p class="text-sm text-theme-muted">
                        {{ item.description }}
                    </p>
                    <p
                        v-if="
                            item.enabledHint &&
                            hasFeature(item) &&
                            isEnabled(item)
                        "
                        class="text-xs text-theme-muted"
                    >
                        {{ item.enabledHint }}
                    </p>
                    <p
                        v-if="!item.comingSoon && isBlockedByProducts(item)"
                        class="text-xs text-theme-warning"
                    >
                        Activa Productos primero.
                    </p>
                </div>

                <span
                    v-if="item.comingSoon"
                    class="whitespace-nowrap text-xs font-medium text-theme-muted"
                >
                    Próximamente
                </span>
                <div
                    v-else-if="!hasFeature(item)"
                    class="flex flex-col items-end gap-1 text-right"
                >
                    <span class="text-xs font-medium text-theme-muted">
                        🔒 No incluida en tu plan
                    </span>
                    <button
                        type="button"
                        class="whitespace-nowrap text-sm text-theme-primary underline"
                        @click="openUpgrade(item)"
                    >
                        Ver planes
                    </button>
                </div>
                <div
                    v-else-if="item.settingsUrl"
                    class="flex flex-col items-end gap-1 text-right"
                >
                    <span
                        class="whitespace-nowrap text-xs font-medium"
                        :class="
                            !item.flagKey || isEnabled(item)
                                ? 'text-theme-success'
                                : 'text-theme-muted'
                        "
                    >
                        {{ item.flagKey ? statusLabel(item) : "✓ Incluida" }}
                    </span>
                    <router-link
                        :to="item.settingsUrl"
                        class="whitespace-nowrap text-sm text-theme-primary underline"
                    >
                        Configurar
                    </router-link>
                </div>
                <div
                    v-else-if="isToggleable(item)"
                    class="flex items-center gap-3"
                >
                    <span
                        class="text-xs font-medium"
                        :class="
                            isEnabled(item)
                                ? 'text-theme-success'
                                : 'text-theme-muted'
                        "
                    >
                        {{ statusLabel(item) }}
                    </span>
                    <custom-switch
                        :value="isEnabled(item)"
                        :disabled="
                            savingKey === item.feature ||
                            isBlockedByProducts(item)
                        "
                        success
                        @input="(value) => saveFlag(item, value)"
                    />
                </div>
                <span
                    v-else
                    class="whitespace-nowrap text-xs font-medium text-theme-success"
                >
                    ✓ Incluida
                </span>
            </div>
        </section>
    </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from "vuex";

import {
    PLAN_FEATURES,
    PLAN_FEATURE_GROUPS,
    canShowUpgrade,
    isFeatureFlagOn
} from "@/common/fields/plan-features";

import CustomSwitch from "@/components/utils/CustomSwitch.vue";

const WATERMARK = "catalogHideWatermark";

export default {
    name: "PlanFeatures",
    components: { CustomSwitch },
    data: () => ({
        flags: {},
        savingKey: null
    }),
    computed: {
        ...mapGetters("control", ["backgroundColor"]),
        ...mapState("auth", ["featureFlags", "permissions"]),
        // Una feature que el rol no tiene solo se lista con su permiso
        // `upgrade:Feature<X>` (portal-sesion-y-candados-optimizacion).
        groups() {
            return PLAN_FEATURE_GROUPS.map((group) => ({
                ...group,
                features: PLAN_FEATURES.filter(
                    (item) =>
                        item.group === group.id &&
                        item.feature !== WATERMARK &&
                        (this.hasFeature(item) ||
                            canShowUpgrade(this.permissions, item.feature))
                )
            })).filter((group) => group.features.length);
        }
    },
    async mounted() {
        const verifyPagePermission =
            this.$ability.verifyPagePermission.bind(this);
        if (
            !(await verifyPagePermission(
                "tenant:GetFeatureFlagsSettings",
                "/app/settings"
            ))
        )
            return;
        await this.loadSettings();
    },
    methods: {
        ...mapActions("tenants", [
            "getFeatureFlagsSettings",
            "updateFeatureFlagsSettings"
        ]),
        ...mapActions("auth", ["applyFeatureFlags"]),
        ...mapActions("platformSubscriptions", ["openPlanUpgrade"]),
        openUpgrade(item) {
            this.openPlanUpgrade({ kind: "feature", feature: item.feature });
        },
        async showError(error) {
            this.$sound.play("error");
            await this.$swal.fire({
                background: this.backgroundColor,
                title: error.title,
                text: error.message,
                icon: error.icon
            });
        },
        hasFeature(item) {
            return this.$ability.hasPermission(item.permission);
        },
        isBlockedByProducts(item) {
            return (
                Boolean(item.requiresProducts) &&
                this.hasFeature(item) &&
                !this.flags.productsEnabled
            );
        },
        isToggleable(item) {
            return Boolean(item.flagKey);
        },
        isEnabled(item) {
            return isFeatureFlagOn(this.flags, item.flagKey);
        },
        statusLabel(item) {
            return this.isEnabled(item) ? "Activada" : "Apagada";
        },
        async loadSettings() {
            try {
                this.flags = await this.$loading.wrap(() =>
                    this.getFeatureFlagsSettings()
                );
            } catch (error) {
                await this.showError(error);
            }
        },
        async saveFlag(item, value) {
            this.savingKey = item.feature;
            try {
                await this.updateFeatureFlagsSettings({
                    [item.flagKey]: value
                });
                this.flags = { ...this.flags, [item.flagKey]: value };
                this.applyFeatureFlags({
                    ...this.featureFlags,
                    [item.flagKey]: value
                });
                this.savingKey = null;
            } catch (error) {
                this.savingKey = null;
                await this.showError(error);
            }
        }
    }
};
</script>
